import "@/main.scss";
import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import * as labs from "vuetify/labs/components";
import type { ThemeDefinition } from "vuetify";
import { paginationOptions } from "@/constants/table";
import { i18n } from "@/plugins/i18n";
import { createVueI18nAdapter } from "vuetify/locale/adapters/vue-i18n";
import { cuiVuetifyIconsSettings } from "@coronet/cui";
import { useI18n } from "vue-i18n";

const light: ThemeDefinition = {
  dark: false,
  colors: {
    primary: "#262260",
    error: "#c90000",
    white: "#FFFFFF",
    anchor: "#006eae",
    inactive: "#BBBBBB",
    link: "#006EAE",
    border: "#262260",
    "on-surface": "#262260",
    "on-white": "#262260",

    "green-base": "#46a762",
    "green-darkest": "#145229",
    "green-dark": "#0f7e34",
    "green-light": "#69d488",
    "green-faint": "#e6f4d8",
    "green-pale": "#EAF5E7",

    "indigo-darkest": "#100e2b",
    "indigo-dark": "#1f1d4a",
    "indigo-medium": "#6a6891",
    "indigo-light": "#b1b1e1",
    "indigo-pale": "#E2E2EB",
    "indigo-faint": "#EEEEF3",
    "on-indigo-faint": "#262260",
    "indigo-instructional": "#EDEDFF",
    "indigo-purple": "#5049A7",
    "indigo-sky": "#E4E4F9",

    "purple-faint": "#F5ECF9",

    "surface-variant": "#B1B1E1",

    "yellow-base": "#be6600",
    "yellow-light": "#f0aa59",
    "yellow-faint": "#f5efe5",
    "yellow-pale": "#FCF9DE",

    "orange-base": "#cf4b00",
    "orange-light": "#ec6b22",
    "orange-faint": "#fef1e1",

    "gray-dark": "#212121",
    "gray-neutral": "#606060",
    "gray-medium": "#bdbdbd",
    "gray-pale": "#ededed",
    "gray-faint": "#f9f9f9",
    "gray-light": "#e0e0e0",

    "red-base": "#f74b4b",
    "red-darkest": "#5e2222",
    "red-dark": "#c92626",
    "red-light": "#ff5c5c",
    "red-faint": "#f5e5e5",
    "red-pale": "#FAE8E6",

    "labels-green-full": "#7bc86c",
    "labels-green-light": "#b8ddb0",
    "labels-yellow-full": "#f5dd2a",
    "labels-yellow-light": "#f5ea92",
    "labels-orange-full": "#ffaf3f",
    "labels-orange-light": "#fbd19c",
    "labels-blue-full": "#28cce5",
    "labels-blue-light": "#8fdfeb",
    "labels-purple-light": "#dfc0eb",
    "labels-purple-full": "#cd8de5",
    "labels-red-full": "#ee7564",
    "labels-red-light": "#efb3ab",

    "blue-light": "#16A4F6",
    "blue-pale": "#DDF5F9",
  },
};

export default createVuetify({
  directives,
  locale: {
    // @ts-expect-error -- vuetify's i18n adapter expects a wider `I18n<any, ...>` shape than vue-i18n's strict generic; safe at runtime
    adapter: createVueI18nAdapter({ i18n, useI18n }),
  },
  display: {
    mobileBreakpoint: "sm",
    thresholds: {
      xs: 0,
      sm: 970,
      md: 1200,
      lg: 1600,
      xl: 1920,
    },
  },
  components: {
    ...labs,
    ...components,
  },
  icons: cuiVuetifyIconsSettings,
  theme: {
    defaultTheme: "light",
    themes: {
      light,
    },
  },
  defaults: {
    VAutocomplete: {
      menuIcon: "icon-arrow-down-xs",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VCombobox: {
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VTextField: {
      variant: "outlined",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
    },
    VCheckbox: {
      trueIcon: "icon-checkbox-marked",
      falseIcon: "icon-checkbox-blank-outline",
      indeterminateIcon: "icon-minus-box",
      color: "primary",
      density: "compact",
      hideDetails: true,
      ripple: false,
    },
    VCheckboxBtn: {
      trueIcon: "icon-checkbox-marked",
      falseIcon: "icon-checkbox-blank-outline",
      indeterminateIcon: "icon-minus-box",
      color: "primary",
      ripple: false,
    },
    VRadioGroup: {
      trueIcon: "icon-radiobox-marked",
      falseIcon: "icon-radiobox-blank",
      density: "compact",
    },
    VRadio: {
      density: "compact",
    },
    VCard: {
      elevation: 0,
      rounded: "lg",
    },
    VTab: {
      height: 32,
    },
    VTabs: {
      nextIcon: "$chevronRight",
      prevIcon: "$chevronLeft",
    },
    VBtn: {
      elevation: 0,
      variant: "flat",
    },
    VSelect: {
      variant: "outlined",
      menuIcon: "icon-arrow-down-xs",
      // remove when this is fixed https://github.com/vuetifyjs/vuetify/issues/22416
      persistentPlaceholder: true,
      clearIcon: "icon-rules-close",
    },
    VDataTable: {
      itemsPerPageOptions: paginationOptions,
    },
    VDataTableServer: {
      rounded: "circle",
      activeColor: "primary",
      color: "primary",
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
      itemsPerPageOptions: paginationOptions,
    },
    VDataTableFooter: {
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
    },
    VPagination: {
      rounded: "circle",
      activeColor: "primary",
      color: "primary",
      prevIcon: "$chevronLeft",
      nextIcon: "$chevronRight",
      firstIcon: "$firstPage",
      lastIcon: "$lastPage",
    },
  },
});
